import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { RoutineTierHint } from '@/components/routines/tier';
import { DataTable } from '@/components/ui/DataTable';
import type { Column } from '@/components/ui/DataTable';
import type {
  RolloutPreviewCustomer,
  RolloutPreviewExcluded,
  RolloutPreviewResponse,
} from '@/hooks/useRollout';

const EXCLUDED_COLUMNS: ReadonlyArray<Column<RolloutPreviewExcluded>> = [
  {
    id: 'host',
    header: 'Host',
    className: 'font-mono text-xs',
    cell: (ex) => ex.hostname,
  },
  {
    id: 'grund',
    header: 'Grund',
    className: 'text-xs text-muted',
    cell: (ex) => ex.reason,
  },
];

/**
 * Vorschau der kundenweiten Ausrollung: Reihenfolge kommt aus `position`
 * (Prioritätsliste), nicht aus Alphabet - sonst wäre die Sortierung gelogen.
 */

interface RolloutPlanTableProps {
  preview: RolloutPreviewResponse;
  excludeSystemIds: string[];
  onToggleExclude: (systemId: string) => void;
}

function byPosition(a: RolloutPreviewCustomer, b: RolloutPreviewCustomer): number {
  return a.position - b.position;
}

export function RolloutPlanTable({
  preview,
  excludeSystemIds,
  onToggleExclude,
}: RolloutPlanTableProps): JSX.Element {
  const customers = [...preview.customers].sort(byPosition);
  const { totals } = preview;

  return (
    <div className="flex flex-col gap-3" data-testid="rollout-plan-table">
      <p className="text-sm" data-testid="rollout-totals">
        <strong>{totals.runs}</strong> {totals.runs === 1 ? 'Lauf' : 'Läufe'} bei{' '}
        <strong>{totals.customers_with_runs}</strong> von {totals.customers}{' '}
        {totals.customers === 1 ? 'Kunde' : 'Kunden'}, {totals.systems}{' '}
        {totals.systems === 1 ? 'System' : 'Systeme'}.
      </p>

      <ul className="flex flex-col gap-2" data-testid="rollout-plan-customers">
        {customers.map((customer) => (
          <CustomerPlanCard
            key={customer.customer_id}
            customer={customer}
            excludeSystemIds={excludeSystemIds}
            onToggleExclude={onToggleExclude}
          />
        ))}
      </ul>
    </div>
  );
}

function CustomerPlanCard({
  customer,
  excludeSystemIds,
  onToggleExclude,
}: {
  customer: RolloutPreviewCustomer;
  excludeSystemIds: string[];
  onToggleExclude: (systemId: string) => void;
}): JSX.Element {
  const skipped = Boolean(customer.skipped_reason);

  return (
    <li
      className={[
        'rounded-md border border-border p-3',
        skipped ? 'bg-surface-2/40 text-muted' : '',
      ].join(' ')}
      data-testid={`rollout-plan-customer-${customer.slug}`}
    >
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span
          className="flex size-6 shrink-0 items-center justify-center rounded-full border border-border bg-surface-2 text-xs font-semibold tabular-nums"
          title={`Position ${customer.position}`}
        >
          {customer.position}
        </span>
        <span className="font-medium text-text">{customer.name}</span>
        <code className="font-mono text-xs text-muted">{customer.slug}</code>
        {skipped && <Badge tone="neutral">uebersprungen</Badge>}
      </div>

      {skipped && (
        <p className="text-sm" data-testid={`rollout-skipped-${customer.slug}`}>
          {customer.skipped_reason}
        </p>
      )}

      {!skipped && (
        <>
          <ul className="flex flex-col gap-2">
            {customer.groups.map((group) => (
              <GroupHosts
                key={group.routine_slug}
                group={group}
                excludeSystemIds={excludeSystemIds}
                onToggleExclude={onToggleExclude}
              />
            ))}
          </ul>
          {customer.excluded.length > 0 && (
            <details className="mt-3 rounded-md border border-border">
              <summary className="cursor-pointer px-3 py-2 text-sm">
                {customer.excluded.length}{' '}
                {customer.excluded.length === 1
                  ? 'System bleibt außen vor'
                  : 'Systeme bleiben außen vor'}
              </summary>
              <DataTable
                caption="Ausgeschlossene Systeme"
                columns={EXCLUDED_COLUMNS}
                rows={customer.excluded}
                rowKey={(ex) => ex.system_id}
                filters={false}
                rowMuted={() => true}
              />
            </details>
          )}
        </>
      )}
    </li>
  );
}

function GroupHosts({
  group,
  excludeSystemIds,
  onToggleExclude,
}: {
  group: RolloutPreviewCustomer['groups'][number];
  excludeSystemIds: string[];
  onToggleExclude: (systemId: string) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const pairs = group.system_ids.map((id, i) => ({
    id,
    hostname: group.hostnames[i] ?? id,
  }));

  return (
    <div
      className="rounded-md border border-border"
      data-testid={`rollout-group-${group.routine_slug}`}
    >
      <div className="flex flex-wrap items-center gap-2 p-2">
        <span className="font-medium">{group.display_name}</span>
        <code className="font-mono text-xs text-muted">{group.routine_slug}</code>
        <RoutineTierHint tier={group.tier} />
        <span className="text-xs text-muted">
          {pairs.length} {pairs.length === 1 ? 'Host' : 'Hosts'}
        </span>
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          rightIcon={
            open ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />
          }
          data-testid={`rollout-group-expand-${group.routine_slug}`}
        >
          {open ? 'Hosts ausblenden' : 'Hosts zeigen'}
        </Button>
      </div>
      {open && (
        <ul className="flex flex-col gap-1 border-t border-border bg-surface-2/40 p-2">
          {pairs.map((sys) => {
            const excluded = excludeSystemIds.includes(sys.id);
            return (
              <li key={sys.id}>
                <label className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-sm hover:bg-surface-2">
                  <input
                    type="checkbox"
                    checked={excluded}
                    onChange={() => onToggleExclude(sys.id)}
                    className="size-4 accent-primary"
                    data-testid={`rollout-exclude-system-${sys.id}`}
                  />
                  <span className="font-mono text-xs">{sys.hostname}</span>
                  <span className="text-xs text-muted">ausnehmen</span>
                </label>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
